import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import Sidebar from '../components/Sidebar';
import MessageList from '../components/MessageList';
import MessageComposer from '../components/MessageComposer';
import ThreadPanel from '../components/ThreadPanel';
import SearchDialog from '../components/SearchDialog';
import TeamDialog from '../components/TeamDialog';
import ChannelDialog from '../components/ChannelDialog';
import UserProfileDialog from '../components/UserProfileDialog';
import ChatInfoPanel from '../components/ChatInfoPanel';
import CallOverlay from '../components/CallOverlay';
import PinnedMessagesPanel from '../components/PinnedMessagesPanel';
import ChannelMembersDialog from '../components/ChannelMembersDialog';
import TodoDialog from '../components/TodoDialog';
import ReminderDialog from '../components/ReminderDialog';
import EventDialog from '../components/EventDialog';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Search, Hash, Lock, Menu, X, Wifi, WifiOff, Phone, Video, Users, MessageCircle, Pin, PanelRightOpen, ListTodo, Bell, CalendarDays } from 'lucide-react';

const ChatPage = () => {
  const { user, logout } = useAuth();
  const {
    connected, activeChannel, activeTeam, channels, messages, typingUsers,
    onlineUsers, unreadCounts, loadTeams, loadChannels, loadDMs, loadMessages,
    joinRoom, leaveRoom, clearUnread, sendTyping, sendWSEvent,
    incomingCall, clearIncomingCall,
    callSignal, clearCallSignal,
    callSignals, consumeCallSignal, clearAllCallSignals,
    setActiveChannel
  } = useChat();

  const [threadMessage, setThreadMessage] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showChannelDialog, setShowChannelDialog] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [viewProfileUserId, setViewProfileUserId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showTodos, setShowTodos] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [callState, setCallState] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const prevChannelRef = useRef(null);
  const activeChannelRef = useRef(null);

  // Keep a ref to activeChannel for notification logic
  useEffect(() => {
    activeChannelRef.current = activeChannel;
  }, [activeChannel]);

  // Initial load
  useEffect(() => {
    loadTeams();
    loadDMs();
  }, [loadTeams, loadDMs]);

  // Load channels when team changes
  useEffect(() => {
    if (activeTeam) {
      loadChannels(activeTeam.team_id);
    }
  }, [activeTeam, loadChannels]);

  // Join/leave WS rooms when channel changes
  useEffect(() => {
    if (activeChannel) {
      const channelId = activeChannel.channel_id || activeChannel.dm_id;
      if (prevChannelRef.current && prevChannelRef.current !== channelId) {
        leaveRoom(prevChannelRef.current);
      }
      joinRoom(channelId);
      loadMessages(channelId);
      clearUnread(channelId);
      prevChannelRef.current = channelId;
    }
  }, [activeChannel, joinRoom, leaveRoom, loadMessages, clearUnread]);

  const currentChannelId = activeChannel?.channel_id || activeChannel?.dm_id;
  const currentTyping = typingUsers[currentChannelId] || {};
  const typingNames = Object.values(currentTyping).filter(name => name !== user?.display_name && name !== user?.username);

  // Keyboard shortcut for search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ===== NOTIFICATIONS =====
  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(perm => {
        setNotificationsEnabled(perm === 'granted');
      });
    } else if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  // In-app toast notifications for messages in non-active channels
  useEffect(() => {
    if (!messages.length) return;
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.sender_id === user?.user_id) return;

    const activeId = activeChannelRef.current?.channel_id || activeChannelRef.current?.dm_id;
    if (lastMsg.channel_id === activeId) return;

    // Check if message has @mention for current user
    const isMentioned = lastMsg.mentions?.some(m => m.user_id === user?.user_id);
    const isDM = lastMsg.channel_id?.startsWith('dm_');

    if (isDM || isMentioned) {
      toast(
        isDM ? `DM from ${lastMsg.sender_name}` : `@${lastMsg.sender_name} mentioned you`,
        {
          description: lastMsg.content?.substring(0, 80) + (lastMsg.content?.length > 80 ? '...' : ''),
          duration: 5000,
        }
      );

      // Browser notification
      if (notificationsEnabled && document.hidden) {
        try {
          new Notification(isDM ? `DM from ${lastMsg.sender_name}` : `Mention from ${lastMsg.sender_name}`, {
            body: lastMsg.content?.substring(0, 100),
            icon: '/favicon.ico'
          });
        } catch (e) {}
      }
    }
  }, [messages, user, notificationsEnabled]);

  // ===== INCOMING CALL HANDLING =====
  useEffect(() => {
    if (!incomingCall) return;
    if (callState && callState.status !== 'idle') {
      clearIncomingCall();
      sendWSEvent({ type: 'call_end', target_user_id: incomingCall.from_user_id });
      return;
    }
    setCallState({
      status: 'ringing',
      isVideo: incomingCall.is_video,
      callerName: incomingCall.caller_name || incomingCall.from_username,
      callerId: incomingCall.from_user_id,
      targetUserId: null,
      isIncoming: true,
      offerSdp: incomingCall.sdp
    });
    clearIncomingCall();
  }, [incomingCall, callState, clearIncomingCall, sendWSEvent]);

  // ===== OUTGOING CALL INITIATION =====
  const startAudioCall = useCallback(() => {
    if (callState) return;
    if (activeChannel?.dm_id) {
      const otherUser = activeChannel.other_user;
      if (!otherUser) return;
      setCallState({
        status: 'ringing', isVideo: false,
        callerName: otherUser.display_name || otherUser.username,
        callerId: user?.user_id, targetUserId: otherUser.user_id,
        isIncoming: false, offerSdp: null,
        scopeId: activeChannel.dm_id, isGroup: false
      });
    } else if (activeChannel?.channel_id) {
      setCallState({
        status: 'ringing', isVideo: false,
        callerName: activeChannel.name,
        callerId: user?.user_id, targetUserId: null,
        isIncoming: false, offerSdp: null,
        scopeId: activeChannel.channel_id, isGroup: true, channelName: activeChannel.name
      });
    }
  }, [activeChannel, user, callState]);

  const startVideoCall = useCallback(() => {
    if (callState) return;
    if (activeChannel?.dm_id) {
      const otherUser = activeChannel.other_user;
      if (!otherUser) return;
      setCallState({
        status: 'ringing', isVideo: true,
        callerName: otherUser.display_name || otherUser.username,
        callerId: user?.user_id, targetUserId: otherUser.user_id,
        isIncoming: false, offerSdp: null,
        scopeId: activeChannel.dm_id, isGroup: false
      });
    } else if (activeChannel?.channel_id) {
      setCallState({
        status: 'ringing', isVideo: true,
        callerName: activeChannel.name,
        callerId: user?.user_id, targetUserId: null,
        isIncoming: false, offerSdp: null,
        scopeId: activeChannel.channel_id, isGroup: true, channelName: activeChannel.name
      });
    }
  }, [activeChannel, user, callState]);

  const endCall = useCallback(() => setCallState(null), []);
  const acceptCall = useCallback(() => setCallState(prev => prev ? { ...prev, status: 'in-call' } : null), []);
  const rejectCall = useCallback(() => {
    if (callState?.callerId) sendWSEvent({ type: 'call_end', target_user_id: callState.callerId });
    setCallState(null);
  }, [callState, sendWSEvent]);
  const onCallConnected = useCallback(() => setCallState(prev => prev ? { ...prev, status: 'in-call' } : null), []);

  const isDM = !!activeChannel?.dm_id;

  return (
    <TooltipProvider>
      <div className="h-screen flex bg-background" data-testid="app-shell">
        {/* Sidebar backdrop for mobile */}
        {sidebarOpen && (
          <div
            className="hidden max-lg:block fixed inset-0 bg-black/20 z-20"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-backdrop"
          />
        )}
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? 'w-[280px] max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:z-30 max-lg:h-full max-lg:shadow-lg' : 'w-0 max-lg:hidden'
          } flex-shrink-0 border-r border-border bg-[hsl(var(--sidebar))] transition-all duration-200 overflow-hidden`}
          data-testid="left-sidebar"
        >
          <Sidebar
            onCreateTeam={() => setShowTeamDialog(true)}
            onCreateChannel={() => setShowChannelDialog(true)}
            onOpenProfile={() => { setViewProfileUserId(null); setShowProfile(true); }}
            onInvite={() => {}}
          />
        </div>

        {/* Main Message Column */}
        <div className="flex-1 flex flex-col min-w-0" data-testid="message-pane">
          {/* Sticky Top Bar */}
          <div className="sticky top-0 z-20 h-14 border-b border-border flex items-center justify-between px-4 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70"
            data-testid="chat-header"
          >
            <div className="flex items-center gap-3">
              <Button
                variant="ghost" size="icon" className="lg:hidden h-8 w-8"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="sidebar-toggle"
              >
                {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </Button>

              {activeChannel && (
                <button
                  className="flex items-center gap-2 hover:bg-[hsl(var(--message-hover))] rounded-lg px-2 py-1 -mx-2 transition-colors cursor-pointer"
                  onClick={() => setShowInfoPanel(true)}
                  data-testid="chat-header-info-trigger"
                >
                  {activeChannel.channel_id ? (
                    <>
                      <div className="w-6 h-6 rounded-md bg-[hsl(var(--surface-2))] flex items-center justify-center">
                        {activeChannel.is_private ? (
                          <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                        ) : (
                          <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="text-left">
                        <h2 className="text-sm font-semibold leading-tight" style={{fontFamily: 'Space Grotesk'}} data-testid="chat-header-channel-title">
                          {activeChannel.name}
                        </h2>
                        {activeChannel.description && (
                          <p className="text-xs text-muted-foreground hidden md:block leading-tight">
                            {activeChannel.description}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="relative">
                        <div className="w-7 h-7 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold">
                          {activeChannel.other_user?.display_name?.[0] || activeChannel.other_user?.username?.[0] || '?'}
                        </div>
                        {onlineUsers[activeChannel.other_user?.user_id] && (
                          <div className="absolute -bottom-0.5 -right-0.5 presence-dot presence-online" style={{width: '9px', height: '9px'}} />
                        )}
                      </div>
                      <div className="text-left">
                        <h2 className="text-sm font-semibold leading-tight" style={{fontFamily: 'Space Grotesk'}} data-testid="chat-header-channel-title">
                          {activeChannel.other_user?.display_name || activeChannel.other_user?.username || 'Direct Message'}
                        </h2>
                        <p className="text-xs text-muted-foreground leading-tight">
                          {onlineUsers[activeChannel.other_user?.user_id] ? 'Online' : 'Offline'}
                        </p>
                      </div>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Channel actions */}
              {activeChannel?.channel_id && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowPinnedPanel(true)} data-testid="pinned-messages-button">
                        <Pin className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Pinned Messages</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowMembersDialog(true)} data-testid="channel-members-button">
                        <Users className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Members</TooltipContent>
                  </Tooltip>
                  <Separator orientation="vertical" className="h-5 mx-1" />
                </>
              )}

              {/* Call buttons (DM and Channel) */}
              {activeChannel && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={startAudioCall} disabled={!!callState} data-testid="audio-call-button">
                        <Phone className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Audio Call</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={startVideoCall} disabled={!!callState} data-testid="video-call-button">
                        <Video className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Video Call</TooltipContent>
                  </Tooltip>
                  <Separator orientation="vertical" className="h-5 mx-1" />
                </>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowSearch(true)} data-testid="search-trigger">
                    <Search className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Search (Ctrl+K)</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowTodos(true)} data-testid="todos-trigger">
                    <ListTodo className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>To-Do Lists</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowReminders(true)} data-testid="reminders-trigger">
                    <Bell className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reminders</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowEvents(true)} data-testid="events-trigger">
                    <CalendarDays className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Events</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={showInfoPanel ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setShowInfoPanel(!showInfoPanel)}
                    data-testid="info-panel-toggle"
                  >
                    <PanelRightOpen className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{showInfoPanel ? 'Close Details' : 'View Details'}</TooltipContent>
              </Tooltip>

              <div className="flex items-center ml-1">
                {connected ? (
                  <Tooltip>
                    <TooltipTrigger><Wifi className="w-3.5 h-3.5 text-[hsl(var(--presence-online))]" /></TooltipTrigger>
                    <TooltipContent>Connected</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger><WifiOff className="w-3.5 h-3.5 text-destructive" /></TooltipTrigger>
                    <TooltipContent>Disconnected</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          {/* Messages Area */}
          {activeChannel ? (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-hidden">
                <MessageList
                  messages={messages.filter(m => !m.parent_id && m.channel_id === currentChannelId)}
                  onThreadClick={(msg) => setThreadMessage(msg)}
                  channelId={currentChannelId}
                  onUserClick={(userId) => { setViewProfileUserId(userId); setShowProfile(true); }}
                />
              </div>

              {/* Typing Indicator */}
              {typingNames.length > 0 && (
                <div className="px-5 py-1.5 text-xs text-muted-foreground flex items-center gap-2" data-testid="typing-indicator">
                  <span className="flex gap-1">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </span>
                  <span>
                    {typingNames.join(', ')} {typingNames.length === 1 ? 'is' : 'are'} typing...
                  </span>
                </div>
              )}

              <MessageComposer channelId={currentChannelId} />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-6">
                <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary)/0.10)] flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-[hsl(var(--primary))]" />
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Welcome to Tello</h3>
                <p className="text-muted-foreground text-sm max-w-xs mx-auto">Select a channel or start a direct message to get the conversation going.</p>
              </div>
            </div>
          )}
        </div>

        {/* Thread Panel */}
        {threadMessage && !showInfoPanel && (
          <div className="w-[360px] border-l border-border bg-card flex-shrink-0" data-testid="thread-pane">
            <ThreadPanel
              parentMessage={threadMessage}
              onClose={() => setThreadMessage(null)}
              channelId={currentChannelId}
            />
          </div>
        )}

        {/* Chat Info Panel (Profile + Shared Media/Docs + Governance) */}
        {showInfoPanel && activeChannel && (
          <div className="w-[340px] max-lg:absolute max-lg:right-0 max-lg:z-30 max-lg:h-full max-lg:shadow-lg flex-shrink-0" data-testid="info-panel-container">
            <ChatInfoPanel
              channel={activeChannel}
              onClose={() => setShowInfoPanel(false)}
              onUserClick={(userId) => { setViewProfileUserId(userId); setShowProfile(true); }}
              onChannelDeleted={() => { setShowInfoPanel(false); setActiveChannel(null); loadChannels(); }}
              onChannelLeft={() => { setShowInfoPanel(false); setActiveChannel(null); loadChannels(); }}
            />
          </div>
        )}

        {/* Call Overlay */}
        {callState && (
          <CallOverlay
            callState={callState}
            onEndCall={endCall}
            onAcceptCall={acceptCall}
            onRejectCall={rejectCall}
            onCallConnected={onCallConnected}
            callSignal={callSignal}
            onCallSignalConsumed={clearCallSignal}
          />
        )}

        {/* Dialogs */}
        <SearchDialog open={showSearch} onClose={() => setShowSearch(false)} />
        <TeamDialog open={showTeamDialog} onClose={() => setShowTeamDialog(false)} />
        <ChannelDialog open={showChannelDialog} onClose={() => setShowChannelDialog(false)} />
        <UserProfileDialog
          open={showProfile}
          onClose={() => { setShowProfile(false); setViewProfileUserId(null); }}
          viewUserId={viewProfileUserId}
        />
        <PinnedMessagesPanel open={showPinnedPanel} onClose={() => setShowPinnedPanel(false)} channelId={currentChannelId} />
        <ChannelMembersDialog open={showMembersDialog} onClose={() => setShowMembersDialog(false)} channelId={currentChannelId} />
        <TodoDialog open={showTodos} onOpenChange={setShowTodos} channelId={activeChannel?.channel_id} dmId={activeChannel?.dm_id} />
        <ReminderDialog open={showReminders} onOpenChange={setShowReminders} channelId={activeChannel?.channel_id} dmId={activeChannel?.dm_id} />
        <EventDialog open={showEvents} onOpenChange={setShowEvents} channelId={activeChannel?.channel_id} dmId={activeChannel?.dm_id} />
      </div>
    </TooltipProvider>
  );
};

export default ChatPage;
